import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { categories, getComicsByCategory } from "@/mocks/comics";

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const activeCategory = categorySlug || "all";
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">(
    "latest"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentPage(1);
  }, [activeCategory]);

  const categoryName =
    categories.find((c) => c.slug === activeCategory)?.name || "全部分类";

  const filteredComics = getComicsByCategory(activeCategory);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === "popular") return b.views - a.views;
    if (sortBy === "rating") return b.rating - a.rating;
    return new Date(b.chapters[0].date).getTime() - new Date(a.chapters[0].date).getTime();
  });

  const totalPages = Math.ceil(sortedComics.length / itemsPerPage);
  const paginatedComics = sortedComics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const siteUrl = "https://hanhanmanhua.com";

  useSEO({
    title: `${categoryName} - 汗汗漫画`,
    description: `汗汗漫画${categoryName}专区，更新最快的${categoryName}资源，海量免费${categoryName}在线阅读，极速更新永久免费。`,
    keywords: `汗汗漫画,${categoryName},免费漫画,在线漫画,${categoryName}推荐`,
    canonicalUrl: `${siteUrl}/category/${activeCategory}`,
  });

  return (
    <main>
      <div className="pt-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类浏览", to: "/category/all" },
            { label: categoryName },
          ]}
        />

        {/* Category Header */}
        <div className="w-full px-4 md:px-8 py-6">
          <div className="max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部漫画作品
            </p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="w-full px-4 md:px-8 border-b border-background-200/50 sticky top-16 z-40 bg-background-50/95 backdrop-blur-md">
          <div className="max-w-[1400px] mx-auto overflow-x-auto">
            <div className="flex items-center gap-0 py-1 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer border-b-2 -mb-[1px] ${
                    activeCategory === cat.slug
                      ? "text-primary-500 border-primary-500"
                      : "text-foreground-500 border-transparent hover:text-foreground-800"
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="w-full px-4 md:px-8 py-4">
          <div className="max-w-[1400px] mx-auto flex items-center gap-3">
            <span className="text-xs text-foreground-500">排序：</span>
            {[
              { key: "latest", label: "最新更新" },
              { key: "popular", label: "最受欢迎" },
              { key: "rating", label: "评分最高" },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key as typeof sortBy)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === s.key
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Comic Grid */}
        <div className="w-full px-4 md:px-8 py-4 pb-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.max(1, p - 1))
                  }
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-sm bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        currentPage === page
                          ? "bg-primary-500 text-white"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-sm bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}